/**
 * Piezas de presentación compartidas del panel admin: badges de estado/perfil,
 * modal de confirmación, envoltorio de carga y formateadores. Sin lógica de
 * negocio (el backend manda); solo pintan datos que ya llegan resueltos.
 */
import type { ReactNode } from "react";
import type { EstadoBateria } from "../../api/types.js";
import s from "./admin.module.css";

/** Estado humano + color del badge de una batería. */
const ESTADO: Record<EstadoBateria, { texto: string; clase: string }> = {
  creada: { texto: "Creada", clase: s.badgeNeutral! },
  mic_check: { texto: "Mic-check", clase: s.badgeNeutral! },
  en_progreso: { texto: "En progreso", clase: s.badgeBlue! },
  completada: { texto: "Completada", clase: s.badgeBlue! },
  procesando: { texto: "Procesando", clase: s.badgeAmber! },
  procesada: { texto: "Procesada", clase: s.badgeGreen! },
  inconcluyente: { texto: "Inconcluyente", clase: s.badgeAmber! },
  suspendida: { texto: "Suspendida", clase: s.badgeAmber! },
  cancelada: { texto: "Cancelada", clase: s.badgeRed! },
};

export function EstadoBadge({ estado }: { estado: EstadoBateria }) {
  const e = ESTADO[estado] ?? { texto: estado, clase: s.badgeNeutral! };
  return <span className={`${s.badge} ${e.clase}`}>{e.texto}</span>;
}

const PERFIL: Record<string, { texto: string; clase: string }> = {
  DESARROLLO_TIPICO: { texto: "Desarrollo típico", clase: s.badgeGreen! },
  DESARROLLO_TIPICO_CON_VIGILANCIA: { texto: "Típico · vigilancia", clase: s.badgeBlue! },
  PERFIL_FONETICO: { texto: "Perfil fonético", clase: s.badgeAmber! },
  PERFIL_FONOLOGICO: { texto: "Perfil fonológico", clase: s.badgeAmber! },
  PERFIL_MIXTO: { texto: "Perfil mixto", clase: s.badgeRed! },
  NO_CONCLUYENTE: { texto: "No concluyente", clase: s.badgeNeutral! },
};

export function PerfilBadge({ profile }: { profile: string | null }) {
  if (!profile) return <span className={s.muted}>—</span>;
  const p = PERFIL[profile] ?? { texto: profile, clase: s.badgeNeutral! };
  return <span className={`${s.badge} ${p.clase}`}>{p.texto}</span>;
}

const ROL: Record<string, string> = { padre: s.badgeBlue!, terapeuta: s.badgePurple!, admin: s.badgeAmber! };
export function RolBadge({ rol }: { rol: string }) {
  return <span className={`${s.badge} ${ROL[rol] ?? s.badgeNeutral!}`}>{rol}</span>;
}

/** Etiqueta inmovible de batería de PRUEBA (QA), sin validez clínica. */
export function QaTag() {
  return <span className={s.qaTag}>QA</span>;
}

// ---- formateadores --------------------------------------------------------
export function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtFechaCorta(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE");
}

/** "6a 3m" a partir de los meses. */
export function edad(meses: number): string {
  return `${Math.floor(meses / 12)}a ${meses % 12}m`;
}

/** Edad aproximada ("6a 3m") a partir de la fecha de nacimiento. */
export function edadDesde(fechaNacimiento: string): string {
  const nac = new Date(fechaNacimiento);
  const ahora = new Date();
  let meses = (ahora.getFullYear() - nac.getFullYear()) * 12 + (ahora.getMonth() - nac.getMonth());
  if (ahora.getDate() < nac.getDate()) meses -= 1;
  return edad(Math.max(0, meses));
}

// ---- modal de confirmación ------------------------------------------------
export function ConfirmDialog({
  titulo,
  cuerpo,
  confirmar,
  peligro,
  ocupado,
  onConfirm,
  onCancel,
}: {
  titulo: string;
  cuerpo: ReactNode;
  confirmar: string;
  peligro?: boolean;
  ocupado?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={s.overlay} role="dialog" aria-modal="true" onClick={onCancel}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalTitle}>{titulo}</div>
        <div className={s.modalBody}>{cuerpo}</div>
        <div className={s.modalActions}>
          <button className={s.btn} onClick={onCancel} disabled={ocupado}>
            Cancelar
          </button>
          <button
            className={`${s.btn} ${peligro ? s.btnDanger : s.btnPrimary}`}
            onClick={onConfirm}
            disabled={ocupado}
          >
            {ocupado ? "…" : confirmar}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Envoltorio de estados de carga/error de una lectura (useQuery). */
export function Async<T>({
  q,
  children,
}: {
  q: { data: T | null; loading: boolean; error: string | null; reload: () => void };
  children: (data: T) => ReactNode;
}) {
  if (q.loading && q.data === null) return <div className={s.empty}>Cargando…</div>;
  if (q.error)
    return (
      <div className={`${s.banner} ${s.bannerError}`}>
        {q.error}{" "}
        <button className={s.btn} style={{ marginLeft: 8 }} onClick={q.reload}>
          Reintentar
        </button>
      </div>
    );
  if (q.data === null) return <div className={s.empty}>Sin datos.</div>;
  return <>{children(q.data)}</>;
}
